/**
 * A bottom sheet for the phone layout.
 *
 * Two heights — a peek that leaves most of the map visible, and a tall state
 * for reading a list — toggled by the grab handle or a drag. It docks above the
 * bottom tab bar (never under the thumb reach) and honours the home-indicator
 * safe area so nothing sits under the gesture bar.
 */

import { useRef, useState, type PointerEvent, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

export type SheetHeight = 'peek' | 'tall';

export function BottomSheet({
  height,
  onHeight,
  header,
  children,
  className,
}: {
  height: SheetHeight;
  onHeight: (height: SheetHeight) => void;
  /** Always-visible strip at the top of the sheet (tabs, selected callsign). */
  header?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const [dragDy, setDragDy] = useState(0);
  const start = useRef<number | null>(null);

  const onDown = (event: PointerEvent) => {
    start.current = event.clientY;
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  };
  const onMove = (event: PointerEvent) => {
    if (start.current === null) return;
    setDragDy(event.clientY - start.current);
  };
  const onUp = () => {
    if (start.current === null) return;
    // A committed flick past the threshold switches height; anything less snaps
    // back. 48px is roughly a thumb-roll, short enough not to fight the map pan.
    if (dragDy < -48) onHeight('tall');
    else if (dragDy > 48) onHeight('peek');
    start.current = null;
    setDragDy(0);
  };

  return (
    <div
      // data-map-panel: on a phone this sheet is most of the map. The command
      // camera measures it so a selected aircraft is not left underneath.
      data-map-panel="bottom-sheet"
      className={cn(
        'phone-sheet glass pointer-events-auto absolute inset-x-2 z-30 flex flex-col overflow-hidden rounded-t-[22px] rounded-b-none',
        className,
      )}
      style={{
        bottom: 'calc(var(--mobile-nav-h) + env(safe-area-inset-bottom))',
        // Both heights live in styles.css, capped so neither reaches the top
        // bar or the emergency controls on a 390px-tall landscape phone.
        height: height === 'tall' ? 'var(--sheet-tall)' : 'var(--sheet-peek)',
        transform: dragDy ? `translateY(${Math.max(0, dragDy)}px)` : undefined,
        transition: start.current === null ? 'height 0.22s ease, transform 0.22s ease' : 'none',
        touchAction: 'none',
      }}
    >
      <button
        type="button"
        aria-label={height === 'tall' ? 'Collapse panel' : 'Expand panel'}
        onClick={() => onHeight(height === 'tall' ? 'peek' : 'tall')}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        className="flex h-6 shrink-0 items-center justify-center"
      >
        <span className="h-1 w-10 rounded-full bg-white/25" />
      </button>
      {header ? <div className="shrink-0 px-1">{header}</div> : null}
      <div className="scroll-thin min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
    </div>
  );
}

export default BottomSheet;
